import {
  array,
  combineLatestTyped,
  promiseState,
  useDataStream,
  useNavigator,
  usePathNameList,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { map, switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import { getListItem } from '../../../global-state/database/common/get-list-item';
import { isItemNotFoundError } from '../../../global-state/database/common/is-item-not-found-error';
import {
  ListItemWithPromiseState,
  listItemWithPromiseStateInit
} from '../../../global-state/database/common/list-item-with-status';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { companyListOf } from '../../../global-state/database/company-list';
import { projectImageListOf } from '../../../global-state/database/project-image-list';
import { projectListOf } from '../../../global-state/database/project-list';
import {
  companyId$,
  koumutenId$,
  projectId$
} from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { pathnameElements, routes } from '../../../routing/routes';
import { ICompanyType } from '../../../types/record/company';
import { IProjectImageInfoType } from '../../../types/record/image-info';
import { IProjectType } from '../../../types/record/project';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ProjectRootView } from './project-root-view';

const pathNameLastToIndex = (pathNameLast: string): number | undefined => {
  switch (pathNameLast) {
    case pathnameElements.summary:
      return 0;
    case pathnameElements.images:
      return 1;
    default:
      return undefined;
  }
};

export const ProjectRoot: FC<{}> = memo(() => {
  const navigator = useNavigator();

  const pathName = usePathNameList();
  const pathNameLast = useMemo(() => array.last(pathName) ?? '', [pathName]);

  const companyList$ = useDataStream<ListWithPromiseState<ICompanyType>>(
    listWithPromiseStateInit<ICompanyType>(),
    koumutenId$.pipe(switchMap(companyListOf))
  );

  const company$ = useDataStream<ListItemWithPromiseState<ICompanyType>>(
    listItemWithPromiseStateInit<ICompanyType>(),
    combineLatestTyped(companyList$, companyId$).pipe(
      map(([list, id]) => getListItem(list, id))
    )
  );

  const projectList$ = useDataStream<ListWithPromiseState<IProjectType>>(
    listWithPromiseStateInit<IProjectType>(),
    companyId$.pipe(switchMap(projectListOf))
  );

  const project$ = useDataStream<ListItemWithPromiseState<IProjectType>>(
    listItemWithPromiseStateInit(),
    combineLatestTyped(projectList$, projectId$).pipe(
      map(([list, id]) => getListItem(list, id))
    )
  );

  const imageList$ = useDataStream<ListWithPromiseState<IProjectImageInfoType>>(
    listWithPromiseStateInit<IProjectImageInfoType>(),
    projectId$.pipe(switchMap(projectImageListOf))
  );

  /* extracted values */

  const company = useStreamValue(company$);
  const project = useStreamValue(project$);
  const imageList = useStreamValue(imageList$);

  const koumutenId = useStreamValue(koumutenId$);
  const companyId = useStreamValue(companyId$);
  const projectId = useStreamValue(projectId$);

  /* handlers */

  // pathnameと対応させるため、routerを使ってタブ切り替えを制御
  const tabIndex = useMemo(
    // dummy comment to control prettier
    () => pathNameLastToIndex(pathNameLast) ?? 0,
    [pathNameLast]
  );

  const tabIndexChange = useCallback(
    (idx: number) => {
      if (
        koumutenId !== undefined &&
        companyId !== undefined &&
        projectId !== undefined
      ) {
        switch (idx) {
          case 0:
            navigator(routes.projectRoot(koumutenId, companyId, projectId));
            break;
          case 1:
            navigator(
              routes.projectImageList(koumutenId, companyId, projectId)
            );
            break;
          default:
            break;
        }
      }
    },
    [navigator, koumutenId, companyId, projectId]
  );

  const onEditProjectImagesClick = useCallback(() => {
    tabIndexChange(1);
  }, [tabIndexChange]);

  useLoadingPage(
    koumutenId === undefined ||
      company?.value.latest === undefined ||
      project?.value.latest === undefined ||
      company?.status === 'pending' ||
      project?.status === 'pending' ||
      imageList?.status === 'pending'
  );

  return (
    <>
      {koumutenId === undefined ||
      company?.value.latest === undefined ||
      project?.value.latest === undefined ||
      imageList === undefined ? (
        undefined
      ) : isItemNotFoundError(company) ? (
        // TODO: エラー表示を整備する
        <NotFoundTemplate message={viewTexts.notFoundMessages.company} />
      ) : isItemNotFoundError(project) ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.project} />
      ) : imageList.status === promiseState.error ? (
        <NotFoundTemplate
          message={viewTexts.notFoundMessages.projectImageList}
        />
      ) : (
        <ProjectRootView
          tabIndex={tabIndex}
          tabIndexChange={tabIndexChange}
          koumutenId={koumutenId}
          company={company.value.latest}
          project={project.value.latest}
          imageList={imageList.value.latest}
          onEditProjectImagesClick={onEditProjectImagesClick}
        />
      )}
    </>
  );
});

ProjectRoot.displayName = 'ProjectRoot';
